import React, { useEffect,useState } from 'react';
import _set from 'lodash/set';
import Proptypes from 'prop-types';

import Table from 'components/customTable';

import LineChart from '../../../components/lineChart';
import { ChartService } from '../../../services/chartService';

const useInnerWidth = () => {
  const [innerWidth, setInnerWidth] = useState(0);
  useEffect(() => {
    function handleResize() {
      setInnerWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return innerWidth;
};

const DATASETS_PROPS = {
  borderWidth: 2,
  pointRadius: 0,
  hitRadius: 3,
  hoverRadius: 3,
  hoverBorderWidth: 1,
  backgroundColor: 'rgba(0, 0, 0, 0)',
};

const races = [
  { race: 'White', color: '#26A69A' },
  { race: 'Asian', color: '#5C6BC0' },
  { race: 'Hispanic or Latino', color: '#AB47BC' },
  { race: 'Black or African American', color: '#FFA726' },
];

const propTypes = {
  jobTitle: Proptypes.string.isRequired,
  raceEthnicitiesOverTime: Proptypes.arrayOf(Proptypes.object).isRequired,
};

const RaceEthnicity = ({ jobTitle, raceEthnicitiesOverTime, ethnicityOverTimeContentBlurb }) => {
  const innerWidth = useInnerWidth();
  const labels = raceEthnicitiesOverTime.map((data) => data.year);
  const { maxTicksLimit, xMin } = getInitialOptions();
  const datasets = races.map(({ race }, idx) => ({
    ...DATASETS_PROPS,
    data: raceEthnicitiesOverTime.map((data) => data[race]),
    label: race,
    datasetKeyProvider: race,
    borderColor: races[idx].color,
    pointBackgroundColor: races[idx].color,
  }));
  const tableData = raceEthnicitiesOverTime.map((x) => ({
    ...x,
    White_Display: (x['White'] * 100).toFixed(2) + '%',
    'Black or African American_Display': (x['Black or African American'] * 100).toFixed(2) + '%',
    Asian_Display: (x['Asian'] * 100).toFixed(2) + '%',
    'Hispanic or Latino_Display': (x['Hispanic or Latino'] * 100).toFixed(2) + '%',
  }));

  const options = {
    onResize: handleHistoryChartResize,
    tooltips: {
      callbacks: {
        label(tooltipItem, data) {
          const { value, datasetIndex } = tooltipItem;
          return { label: data.datasets[datasetIndex].label, value };
        },
      },
      custom: renderChartTooltip,
    },
    scales: {
      yAxes: [
        {
          ticks: {
            maxTicksLimit,
            callback(value) {
              return `${(value * 100).toFixed(0)}%`;
            },
          },
        },
      ],
      xAxes: [
        {
          ticks: {
            xMin,
            maxTicksLimit,
          },
        },
      ],
    },
  };

  function renderChartTooltip(tooltipModel) {
    // Tooltip Element
    let tooltipEl = document.getElementById('chartjs-tooltip');

    // Create element on first render
    if (!tooltipEl) {
      tooltipEl = document.createElement('div');
      tooltipEl.id = 'chartjs-tooltip';
      tooltipEl.innerHTML = '<table></table>';
      document.body.appendChild(tooltipEl);
    }

    // Hide if no tooltip
    if (tooltipModel.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    // Set caret Position
    tooltipEl.classList.remove(...tooltipEl.classList);
    if (tooltipModel.yAlign) {
      tooltipEl.classList.add(tooltipModel.yAlign);
    }
    if (tooltipModel.xAlign) {
      tooltipEl.classList.add(tooltipModel.xAlign);
    }

    // Set Text
    if (tooltipModel.body) {
      const titleLines = tooltipModel.title || [];
      const bodyLines = tooltipModel.body
        .map((bodyItem) => bodyItem.lines[0])
        .sort((a, b) => b.value - a.value);

      const newBodyLines = bodyLines.map(
        ({ label, value }) => `${label}: <strong>${(value * 100).toFixed(0)}%</strong>`
      );

      let innerHtml = '<thead>';

      titleLines.forEach(function (title) {
        innerHtml += '<tr><th>' + title + '</th></tr>';
      });
      innerHtml += '</thead><tbody>';

      bodyLines.forEach(function (_, i) {
        const index = races.findIndex(({ race }) => race === bodyLines[i].label);
        let style = 'background:' + races[index].color;
        const span = '<span style="' + style + '" class="tooltip-key-color"></span>';
        innerHtml += '<tr><td>' + span + newBodyLines[i] + '</td></tr>';
      });
      innerHtml += '</tbody>';

      const tableRoot = tooltipEl.querySelector('table');
      tableRoot.innerHTML = innerHtml;
    }

    // `this` will be the overall tooltip
    const position = this._chart.canvas.getBoundingClientRect();

    // set display and position styles
    tooltipEl.style.opacity = 1;
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
    tooltipEl.style.pointerEvents = 'none';
  }

  function getInitialOptions() {
    if (innerWidth < 768) {
      return { maxTicksLimit: 6, xMin: labels[1] };
    } else {
      return { maxTicksLimit: 11, xMin: labels[0] };
    }
  }

  function handleHistoryChartResize(chartInstance) {
    const { maxTicksLimit, xMin } = getInitialOptions();
    _set(chartInstance, 'options.scales.yAxes[0].ticks.maxTicksLimit', maxTicksLimit);
    _set(chartInstance, 'options.scales.xAxes[0].ticks.maxTicksLimit', maxTicksLimit + 1);
    _set(chartInstance, 'options.scales.xAxes[0].ticks.min', xMin);
  }

  return (
    <section id="race-ethnicity-over-time">
      <h3 className="cmpSectionh4 mb-3">{`${jobTitle} Race And Ethnicity Over Time`}</h3>
      {ethnicityOverTimeContentBlurb && ethnicityOverTimeContentBlurb.content && (
        <div
          className="mb-4"
          dangerouslySetInnerHTML={{ __html: ethnicityOverTimeContentBlurb.content }}
        />
      )}
      <LineChart
        {...ChartService.setupLineChart({
          labels,
          datasets,
          options,
        })}
      />
      <div className="mb-0 z-mt-48">
        <Table
          columnConfig={{
            year: {
              columnName: 'Year',
              defaultSortDirection: 'desc',
            },
            White_Display: {
              columnName: 'White',
              customSortByField: 'White',
            },
            'Black or African American_Display': {
              columnName: 'Black or African American',
              customSortByField: 'Black or African American',
            },
            Asian_Display: {
              columnName: 'Asian',
              customSortByField: 'Asian',
            },
            'Hispanic or Latino_Display': {
              columnName: 'Hispanic or Latino',
              customSortByField: 'Hispanic or Latino',
            },
          }}
          bodyData={tableData}
          defaultRowsDisplay={99}
          scrollOnMobile
          tableHeaderTitleContent={
            <h3 className="zp-title-h3 text-capitalize z-mb-24">{`${jobTitle} Race By Year`}</h3>
          }
        />
      </div>
    </section>
  );
};

RaceEthnicity.propTypes = propTypes;

export default RaceEthnicity;
